import { Component, Output, EventEmitter, Input, OnInit, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FuelConsumptionModal } from '../../modals/fuel-consumption-modal/fuel-consumption-modal.component';
import { FuelConsumptionService } from '../../../services/fuel-consumption.service';
import { FuelKpiRequest } from '../../../../_shared';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { KpiWidgetComponent, DropdownOption } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-fuel-consumption-widget',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoPipe],
  templateUrl: './fuel-consumption-widget.component.html',
  styleUrl: './fuel-consumption-widget.component.scss'
})
export class FuelConsumptionWidgetComponent extends BaseWidgetComponent implements OnInit {

  @Input() selectedFilter: string = "";
  @Output() openModal = new EventEmitter<void>();

  private transloco = inject(TranslocoService);

  icon = 'assets/images-fuel/fuelConsumption.svg';
  cost: number;
  hasError: boolean = false;
  currencyCode: string = '';
  currencySymbol: string = '';
  errorMessage: string = '';
  dropdownOptions: DropdownOption[] = [];
  selectedUnit = MeasurementTypes.Volume.toString();

  constructor(
    filterService: FilterService,
    private modalService: NgbModal,
    private fuelConsumptionService: FuelConsumptionService,
    private errorHandlerService: ErrorHandlerService,
    private auth: AuthService
  ) {
    super(filterService);
  }

  ngOnInit(): void {
    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    this.dropdownOptions = [
      { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
      { label: this.transloco.translate('fuelDashboard.units.kmPerLitres'), value: MeasurementTypes.FuelEconomy.toString(), unit: 'km/ℓ' },
      { label: this.transloco.translate('fuelDashboard.units.litresPer100Km'), value: MeasurementTypes.FuelConsumption.toString(), unit: 'ℓ/100km' },
      { label: this.transloco.translate('fuelDashboard.units.costPerKm', { currency: this.currencyCode }), value: MeasurementTypes.CostPerUnit.toString(), unit: '/km' }
    ];
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.loadFuelConsumptionData();
  }

  private fuelConsumptionModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(FuelConsumptionModal, modalOptions);
    modalRef.componentInstance.value = 100;
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  public onWidgetClick(): void {
    // this.fuelConsumptionModal()
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;
    this.isLoading = true;
    await this.loadFuelConsumptionData();
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  private async loadFuelConsumptionData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: FuelKpiRequest = {
        fuelMeasurementType: this.getEnumFromString(this.selectedUnit),
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
      };

      this.cancelSubject.next();
      const response = await this.fuelConsumptionService.getFuelConsumptionSummary(request, this.cancelSubject);

      if (response.data) {
        const data = response.data;
        this.value = data.totalValue;
        this.percentageDifference = data.percentageChange;
        this.cost = data.totalCost;
        this.difference = data.difference;
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private getEnumFromString(value: string): MeasurementTypes {
    const enumValue = parseInt(value) as MeasurementTypes;
    const isValid = Object.values(MeasurementTypes).includes(enumValue);
    return isValid ? enumValue : MeasurementTypes.Volume;
  }
}